var _a, _b, _c, _d;
import { CssBaseline, ThemeProvider } from '@mui/material';
import { enUS as muiEnUs } from '@mui/material/locale';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enUS as muiDatePickersEnUs } from '@mui/x-date-pickers/locales';
import { LicenseInfo } from '@mui/x-license';
import { enUS } from 'date-fns/locale/en-US';
import React from 'react';
import { hideInitialGlobalLoader } from '../GlobalLoader';
import { createJuvareTheme } from '../JuvareTheme';
import { useCommonWebTranslation } from '../hooks';
import { setDefaultDateConfig } from '../utils';
import { FeatureFlagConfigChangeContext } from './FeatureFlagConfigChangeContext';
// Set the Material-UI X license key
if (!((_b = (_a = window.juvareLicenses) === null || _a === void 0 ? void 0 : _a.muiXPro) === null || _b === void 0 ? void 0 : _b.licenseKey))
    console.error('muiXPro license is missing under juvareLicenses object');
LicenseInfo.setLicenseKey(((_d = (_c = window.juvareLicenses) === null || _c === void 0 ? void 0 : _c.muiXPro) === null || _d === void 0 ? void 0 : _d.licenseKey) ||
    '76010dc254cea9c5061b42a0bbfca873Tz01MzczOCxFPTE3NjI1Mzc5Nzk1MDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI=');
const initialFeatureFlagConfigKey = Date.now();
/**
 * Provides global configuration for theme, localization, date formatting, and feature flags
 * across the application. Initializes context providers and applies Material UI baseline styling.
 * @param {DefaultConfigProviderProps} props - Configuration props for theming, localization, and children rendering.
 * @returns {React.ReactElement} The provider-wrapped React component tree.
 */
export const CommonProvider = (props) => {
    var _a;
    const { t, i18n } = useCommonWebTranslation();
    const appTheme = React.useMemo(() => {
        var _a, _b;
        const localization = findLocalization(i18n.language);
        const muiDatePickersLocalization = (_a = props.muiDatePickersLocalization) !== null && _a !== void 0 ? _a : localization === null || localization === void 0 ? void 0 : localization.muiDatePickersLocalization;
        const muiLocalization = (_b = props.muiLocalization) !== null && _b !== void 0 ? _b : localization === null || localization === void 0 ? void 0 : localization.muiLocalization;
        const args = [];
        if (muiDatePickersLocalization) {
            args.push(muiDatePickersLocalization);
        }
        if (muiLocalization) {
            args.push(muiLocalization);
        }
        return createJuvareTheme(props.customizeThemeOptions, ...args);
    }, [props.customizeThemeOptions, props.muiDatePickersLocalization, props.muiLocalization, i18n.language]);
    const dateConfig = React.useMemo(() => {
        const localization = findLocalization(i18n.language);
        const dateConfig = Object.assign({}, props.dateConfig);
        if (!dateConfig.dateFormat) {
            dateConfig.dateFormat = t('dateFormats.dateFormat');
        }
        if (!dateConfig.dateTimeFormat) {
            dateConfig.dateTimeFormat = t('dateFormats.dateTimeFormat');
        }
        if (!dateConfig.formatOptions) {
            dateConfig.formatOptions = {};
        }
        if (!dateConfig.formatOptions.locale && (localization === null || localization === void 0 ? void 0 : localization.dateLocale)) {
            dateConfig.formatOptions.locale = localization.dateLocale;
        }
        setDefaultDateConfig(dateConfig);
        return dateConfig;
    }, [props.dateConfig, t, i18n.language]);
    const muiDateFormats = React.useMemo(() => {
        const muiDateFormats = Object.assign({}, props.muiDateFormats);
        if (!muiDateFormats.keyboardDate) {
            const value = t('dateFormats.keyboardDate');
            if (value) {
                muiDateFormats.keyboardDate = value;
            }
        }
        if (!muiDateFormats.keyboardDateTime) {
            const value = t('dateFormats.keyboardDateTime');
            if (value) {
                muiDateFormats.keyboardDateTime = value;
            }
        }
        if (!muiDateFormats.keyboardDateTime12h) {
            const value = t('dateFormats.keyboardDateTime12h');
            if (value) {
                muiDateFormats.keyboardDateTime12h = value;
            }
        }
        if (!muiDateFormats.keyboardDateTime24h) {
            const value = t('dateFormats.keyboardDateTime24h');
            if (value) {
                muiDateFormats.keyboardDateTime24h = value;
            }
        }
        return muiDateFormats;
    }, [props.muiDateFormats, t]);
    React.useEffect(() => {
        hideInitialGlobalLoader();
    }, []);
    const [featureFlagConfigKey, setFeatureFlagConfigKey] = React.useState(initialFeatureFlagConfigKey);
    const featureFlagConfigChangeContextValue = React.useMemo(() => ({
        configKey: featureFlagConfigKey,
        onConfigChanged: () => setFeatureFlagConfigKey((prev) => Math.max(prev + 1, Date.now())),
    }), [featureFlagConfigKey]);
    return (React.createElement(ThemeProvider, { theme: appTheme },
        React.createElement(CssBaseline, null),
        React.createElement(LocalizationProvider, { dateAdapter: AdapterDateFns, adapterLocale: (_a = dateConfig.formatOptions) === null || _a === void 0 ? void 0 : _a.locale, dateFormats: muiDateFormats },
            React.createElement(FeatureFlagConfigChangeContext.Provider, { value: featureFlagConfigChangeContextValue }, props.children))));
};
/**
 * Map of localization objects. Only 'en' locale is supported out of box.
 *
 * If you need to support more locales add more items to this map. Make sure that you add to this map before
 * `CommonProvider` is rendered.
 *
 * Example code to add French language:.
 * ```
 * import { localizationMap } from '@juvare/common-web-ui/CommonProvider';
 * import { frFR as muiFrFr } from '@mui/material/locale';
 * import { frFR as muiDataPickersFrFr } from '@mui/x-date-pickers/locales';
 * import dateFr from 'date-fns/locale/fr';
 *
 * localizationMap.fr = {
 *     dateLocale: dateFr,
 *     muiLocalization: muiFrFr,
 *     muiDatePickersLocalization: muiDataPickersFrFr,
 * };
 * ```
 */
export const localizationMap = {
    en: {
        dateLocale: enUS,
        muiLocalization: muiEnUs,
        muiDatePickersLocalization: muiDatePickersEnUs,
    },
};
/**
 * Looks up a localization object by language code, falling back to the base language if needed.
 * @param {string} language - The language code (e.g. "en-US" or "fr").
 * @returns {unknown} The corresponding localization object if found; otherwise, undefined.
 */
function findLocalization(language) {
    let localization = localizationMap[language];
    if (!localization && language.includes('-')) {
        localization = localizationMap[language.split('-')[0]];
    }
    return localization;
}
